import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import Table from '../components/Table';
import { getUsers, User, UserPoints } from '../api/usersApi';
import { createSideBet, getSideBetPoints, getSideBets, SideBet, updateSideBet, deleteSideBet } from '../api/sideBetsApi';
import { useAuth } from '../context/AuthContext';
import { Plus, X, Search, Check } from 'lucide-react';
import { getUserPointsColumns } from '../utils/userPointsTableUtils';

const SideBets: React.FC = () => {
  const { user } = useAuth();
  const [sideBets, setSideBets] = useState<SideBet[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [pointsBySideBet, setPointsBySideBet] = useState<Record<number, UserPoints[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [formName, setFormName] = useState<string>('');
  const [formUserIDs, setFormUserIDs] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [sb, us] = await Promise.all([getSideBets(), getUsers()]);
        setSideBets(sb);
        setUsers(us);
        await fetchPointsParallel(sb);
        setError(null);
      } catch (err) {
        console.error('Failed to load side bets', err);
        setError('Failed to load side bets');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fetchPointsParallel = async (bets: SideBet[]) => {
    const entries = await Promise.all(
      bets.map(async (sb) => {
        try {
          const pts = await getSideBetPoints(sb.id);
          return [sb.id, pts] as [number, UserPoints[]];
        } catch {
          return [sb.id, []] as [number, UserPoints[]];
        }
      })
    );
    const map: Record<number, UserPoints[]> = {};
    for (const [id, pts] of entries) {
      map[id] = pts;
    }
    setPointsBySideBet(map);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: formName.trim(), user_ids: formUserIDs };
      if (editingId) {
        await updateSideBet(editingId, payload);
      } else {
        await createSideBet(payload);
      }
      setShowModal(false);
      setFormName('');
      setFormUserIDs([]);
      setEditingId(null);
      setSearchQuery('');
      const updated = await getSideBets();
      setSideBets(updated);
      await fetchPointsParallel(updated);
      setError(null);
    } catch (err) {
      console.error('Failed to save side bet', err);
      setError('Failed to save side bet');
    } finally {
      setSubmitting(false);
    }
  };

  const ensurePoints = async (sideBetId: number): Promise<UserPoints[]> => {
    if (pointsBySideBet[sideBetId]) {
      return pointsBySideBet[sideBetId];
    }
    const pts = await getSideBetPoints(sideBetId);
    setPointsBySideBet((prev) => ({ ...prev, [sideBetId]: pts }));
    return pts;
  };

  const handleEdit = async (sb: SideBet) => {
    setEditingId(sb.id);
    setFormName(sb.name);
    const pts = await ensurePoints(sb.id);
    if (pts && pts.length > 0) {
      setFormUserIDs(pts.map((p) => p.id));
    } else {
      setFormUserIDs(sb.user_ids || []);
    }
    setSearchQuery('');
    setShowModal(true);
  };

  const handleDelete = async (sb: SideBet) => {
    if (!window.confirm(`Are you sure you want to delete "${sb.name}"?`)) {
      return;
    }
    try {
      await deleteSideBet(sb.id);
      const updated = await getSideBets();
      setSideBets(updated);
      await fetchPointsParallel(updated);
    } catch (err) {
      console.error('Failed to delete side bet', err);
      setError('Failed to delete side bet');
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormUserIDs([]);
    setSearchQuery('');
    setShowModal(true);
  };

  const toggleUser = (id: number) => {
    setFormUserIDs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u =>
      u.id !== user?.id &&
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [users, searchQuery, user?.id]);

  const columns = useMemo(() => getUserPointsColumns(), []);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64 text-gray-300">Loading side bets...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">בית צדדי</h1>
            <p className="text-gray-400">צור בית צדדי עם חברים</p>
          </div>
        </div>

        {/* Create New Button */}
        <button
          onClick={handleCreate}
          className="mb-8 bg-primary-600 hover:bg-primary-500 text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 shadow-lg transition-all"
        >
          <Plus size={20} />
          צור בית צדדי חדש
        </button>

        {/* Side Bet Tables */}
        <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start">
          {sideBets.map((sb) => (
            <div key={sb.id} className="w-full lg:w-auto">
              <Table
              className="w-full sm:w-80"
                title={sb.name}
                data={pointsBySideBet[sb.id] || []}
                columns={columns}
                keyExtractor={(item) => `${sb.id}-${item.id}`}
                isLoading={!pointsBySideBet[sb.id]}
                highlightCondition={(item) => item.id === user?.id}
                withRanks
                emptyMessage="No points yet"
                compact
                tableFixed
                actions={{
                  onEdit: () => handleEdit(sb),
                  onDelete: () => handleDelete(sb)
                }}
              />
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-stake-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-stake-border">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-stake-border">
                <h2 className="text-2xl font-bold text-white">
                  {editingId ? 'ערוך בית' : 'צור בית חדש'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <form onSubmit={handleSubmit}>
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                  {error && <div className="text-stake-red mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">{error}</div>}
                  
                  {/* Group Name */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      שם בית
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="הכנס שם בית..."
                      className="w-full bg-dark-100 border border-stake-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-stake-blue"
                    />
                  </div>

                  {/* User Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      בחר משתמשים ({formUserIDs.length} נבחרו)
                    </label>
                    
                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="חפש משתמשים..."
                        className="w-full bg-dark-100 border border-stake-border rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-stake-blue"
                      />
                    </div>

                    {/* User List */}
                    <div className="space-y-2 max-h-64 overflow-y-auto bg-dark-100 rounded-lg p-3 border border-stake-border">
                      {filteredUsers.map(u => {
                        const isSelected = formUserIDs.includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'bg-stake-blue/20 border border-stake-blue/50'
                                : 'bg-dark-200 hover:bg-stake-card border border-transparent'
                            }`}
                          >
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-600 to-stake-blue flex items-center justify-center text-white font-semibold text-sm">
                                {u.first_name[0]}{u.last_name[0]}
                              </div>
                              {isSelected && (
                                <div className="absolute -top-1 -right-1 bg-stake-blue rounded-full p-0.5">
                                  <Check size={12} className="text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-white font-medium">
                                {u.first_name} {u.last_name}
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleUser(u.id)}
                              className="w-5 h-5 rounded border-stake-border text-stake-blue focus:ring-stake-blue focus:ring-offset-dark-100"
                            />
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      💡 אתה מתווסף אוטומטית לבית
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-stake-border bg-dark-100">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2.5 text-gray-300 hover:text-white transition-colors font-medium"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    disabled={!formName.trim() || formUserIDs.length === 0 || submitting}
                    className="bg-primary-600 hover:bg-primary-500 text-white font-semibold px-8 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                  >
                    {submitting ? 'שומר...' : editingId ? 'שמור שינויים' : 'צור בית'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SideBets;